import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import RefreshButton from '@/components/Buttons/RefreshButton';
import SearchIcon from '@/components/Icons/SearchIcon';
import TypeIcon from '@/components/Icons/TypeIcon';
import { CREATIVE_TYPES_ICONS, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { Add } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControl,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { ChangeEvent, memo } from 'react';
import CreativeChips from './CreativesChips';

interface ICreativesModalSearchProps {
  searchValue: string;
  typeOptions: CreativeType[];
  selectedTypes: CreativeType[];
  handleSearch: (event: ChangeEvent<HTMLInputElement>) => void;
  handleTypeChange: (type: CreativeType) => void;
  onAddCreativeClick?: () => void;
  onRefreshClick: () => void;
}

const CreativesModalSearch = memo(
  ({
    searchValue,
    typeOptions,
    selectedTypes,
    handleSearch,
    handleTypeChange,
    onAddCreativeClick,
    onRefreshClick,
  }: ICreativesModalSearchProps) => {
    return (
      <div className='flex flex-col mt-5 gap-4'>
        <div className='flex w-full items-center justify-between flex-row'>
          <div className='flex w-[70%]'>
            <div className='flex h-[40px] w-[70%] items-center gap-2 rounded-md px-3 py-2 border shadow-sm bg-[#F8F8F8] border-[#EAEAEA]'>
              <SearchIcon />
              <input
                className='w-full outline-none bg-[#F8F8F8]'
                value={searchValue}
                onChange={handleSearch}
                placeholder='Search Creative'
                autoComplete='off'
              />
            </div>
            <FormControl variant='outlined'>
              <Select
                labelId='advertiser-dropdown-label'
                input={<OutlinedInput className='h-full' />}
                id='advertiser-dropdown'
                sx={{ width: '120px', height: 40, marginLeft: '12px' }}
                renderValue={() => (
                  <span>
                    <span className='flex flex-direction items-center'>
                      <ListItemIcon className='mr-[-28px]'>
                        <TypeIcon />
                      </ListItemIcon>
                      <span className='text-[#1B4332] text-base font-semibold'>Type</span>
                    </span>
                  </span>
                )}
                multiple
                value={[true]}
              >
                {typeOptions.map(type => {
                  const Icon = CREATIVE_TYPES_ICONS[type];
                  return (
                    <MenuItem
                      key={type}
                      value={type}
                      onClick={() => handleTypeChange(type)}
                      disabled={selectedTypes.length === 1 && selectedTypes[0] === type}
                    >
                      <Checkbox
                        checked={selectedTypes.includes(type)}
                        size='medium'
                        sx={{
                          padding: 0,
                          marginRight: '7px',
                        }}
                        disableRipple
                      />
                      <ListItemIcon>
                        <Icon width='20' />
                      </ListItemIcon>
                      <ListItemText primary={CREATIVE_TYPES_TITLES[type]} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
          {!!onAddCreativeClick && (
            <Button
              variant='contained'
              sx={{
                height: '40px',
                width: '200px',
              }}
              onClick={onAddCreativeClick}
            >
              <Add sx={{ width: 20, height: 20, marginRight: '4px' }} />
              Add New Creative
            </Button>
          )}
        </div>
        <div className='flex w-full h-[38px] justify-between'>
          <CreativeChips selectedTypes={selectedTypes} handleTypeChange={handleTypeChange} />
          <RefreshButton onClick={onRefreshClick} />
        </div>
      </div>
    );
  },
);

export default CreativesModalSearch;
